import { z } from 'zod';
import { HttpErrorResponse } from '../common/validation-error';
import { PreviewQuestion, SerializedDate } from '../preview/get-activity';
const CompanySlug = z.string().min(1);
const ProjectSlug = z.string().min(1);
const AssessmentTestSlug = z.string().min(1);
const AnswerStateResponse = z
    .object({
    value: z.string(),
    is_correct: z.boolean().nullable(),
    is_submitted: z.boolean(),
    submitted_at: z.string().nullable(),
})
    .passthrough();
const AIReviewResponse = z.object({
    lesson_takeaways: z.array(z.string()),
    progress_suggestions: z.string().nullable(),
    further_insights: z.string().nullable(),
});
const MentorReviewResponse = z
    .object({
    questionId: z.union([z.string(), z.number()]),
    isRequested: z.boolean(),
    review: z.string().nullable(),
    requestedAt: z.string().optional(),
    comment: z.string().nullable(),
    reviewedAt: z.string().nullable().optional(),
    repliedBy: z
        .object({
        user_id: z.number(),
        name: z.string(),
        email: z.string(),
    })
        .nullable()
        .optional(),
})
    .passthrough();
const TeamsProjectSessionResponse = z.object({
    id: z.number(),
    created_at: SerializedDate,
    updated_at: SerializedDate,
    seconds_spent: z.number(),
    feedback: z.string(),
    difficulty_rating: z.number(),
    answer_states: z.record(AnswerStateResponse),
    finished_at: SerializedDate.nullable(),
    ai_review: AIReviewResponse.nullable(),
    mentor_reviews: z.array(MentorReviewResponse),
});
const AppliedProjectTagResponse = z.object({
    value: z.string(),
    type: z.enum(['TOPIC', 'ROLE', 'APPLICATION']),
});
const AssessmentOptionResponse = z.object({
    id: z.string(),
    content: z.string(),
});
const AssessmentSectionBase = z.object({
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
});
const AssessmentTestSectionResponse = z.discriminatedUnion('type', [
    AssessmentSectionBase.extend({
        type: z.literal('text-input'),
        placeholder: z.string().nullable(),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('ranking'),
        options: z.array(AssessmentOptionResponse),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('multiple-choice'),
        isSingleChoice: z.boolean(),
        options: z.array(AssessmentOptionResponse),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('matching'),
        leftItems: z.array(AssessmentOptionResponse),
        rightItems: z.array(AssessmentOptionResponse),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('fill-in-the-gap'),
        blanks: z.array(z.object({ id: z.string(), content: z.string(), placeholder: z.string() })),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('py-playground'),
        setupCode: z.string(),
        startingCode: z.string(),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('llm-playground'),
        initialPrompt: z.string(),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('multiple-rating'),
        questions: z.array(z.object({
            id: z.string(),
            content: z.string(),
            options: z.array(z.string()),
        })),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('py-editor'),
        startingCode: z.string(),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('llm-editor'),
        initialPrompt: z.string(),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('text-template'),
        template: z.string(),
        blanks: z.array(z.object({ id: z.string(), content: z.string(), placeholder: z.string() })),
    }),
    AssessmentSectionBase.extend({
        type: z.literal('matrix-ranking'),
        items: z.array(z.string()),
        horizontalAxisLabel: z.string(),
        verticalAxisLabel: z.string(),
        annotationPrompt: z.string(),
    }),
]);
const AssessmentTestStepResponse = z.object({
    id: z.string(),
    title: z.string(),
    label: z.string(),
    introduction: z.string().nullable(),
    sections: z.array(AssessmentTestSectionResponse),
    objectives: z.array(z.object({
        id: z.string(),
        description: z.string(),
        facets: z.array(z.object({ questions: z.array(z.string()), description: z.string() })),
    })),
});
const AssessmentTimeIntervalResponse = z.object({
    startedAt: z.string(),
    endedAt: z.string().nullable(),
});
const AssessmentAnswerBase = z.object({
    id: z.string(),
    timeIntervals: z.array(AssessmentTimeIntervalResponse),
    didInteract: z.boolean(),
    isAnswered: z.boolean(),
    followUpAnswer: z.string().nullable(),
});
const AssessmentAnswerOption = z.array(AssessmentOptionResponse);
const AssessmentTestAnswerSectionResponse = z.discriminatedUnion('type', [
    AssessmentAnswerBase.extend({
        type: z.literal('text-input'),
        answer: z.string(),
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('ranking'),
        answer: AssessmentAnswerOption,
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('multiple-choice'),
        answer: AssessmentAnswerOption,
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('matching'),
        answer: z.array(z.object({ leftId: z.string(), rightId: z.string() })),
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('fill-in-the-gap'),
        answer: AssessmentAnswerOption,
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('py-playground'),
        answer: z.object({ output: z.string(), code: z.string() }),
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('llm-playground'),
        answer: z.object({ prompt: z.string(), output: z.string() }),
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('multiple-rating'),
        answer: z.array(z.object({ id: z.string(), label: z.string() })),
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('py-editor'),
        answer: z.string(),
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('llm-editor'),
        answer: z.string(),
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('text-template'),
        answer: AssessmentAnswerOption,
    }),
    AssessmentAnswerBase.extend({
        type: z.literal('matrix-ranking'),
        answer: z.array(z.object({
            item: z.string(),
            x: z.number(),
            y: z.number(),
            note: z.string(),
        })),
    }),
]);
export const AssessmentTestAnswerResponse = z.object({
    id: z.string(),
    introduction: z
        .object({ timeIntervals: z.array(AssessmentTimeIntervalResponse) })
        .nullable(),
    sections: z.array(AssessmentTestAnswerSectionResponse),
});
export const GetTeamsProjectResponse = z
    .object({
    activity_id: z.number(),
    activity_session_id: z.number(),
    title: z.string(),
    summary: z.string().nullable(),
    slug: z.string(),
    latest_submission: TeamsProjectSessionResponse,
    exercise: z
        .object({
        id: z.number(),
        created_at: SerializedDate,
        updated_at: SerializedDate,
        slug: z.string(),
        topic: z.string(),
        name: z.string(),
        summary: z.string(),
        description: z.string(),
        lesson: z.string(),
        is_draft: z.boolean(),
        tags: z.array(AppliedProjectTagResponse),
        image_url: z.string().nullable(),
        metadata: z.record(z.unknown()).nullable(),
        company_id: z.number().nullable(),
        objectives: z.unknown(),
        questions: z.array(PreviewQuestion),
    })
        .passthrough(),
    content_slug: z.string(),
    content_title: z.string(),
})
    .passthrough();
export const getTeamsProjectRoute = {
    version: 1,
    method: 'GET',
    path: '/teams/{companySlug}/projects/{projectSlug}',
    name: 'get-teams-project',
    request: {
        params: z.object({ companySlug: CompanySlug, projectSlug: ProjectSlug }),
    },
    responses: {
        200: GetTeamsProjectResponse,
        400: HttpErrorResponse,
        404: HttpErrorResponse,
    },
};
export const GetTeamsProjectsResponse = z.array(z
    .object({
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    image_url: z.string(),
    topic: z.string(),
    is_submitted: z.boolean(),
    is_finished: z.boolean(),
    is_joined: z.boolean().optional(),
    question_count: z.number(),
    completedQuestions: z.number().nullable(),
    tags: z.array(AppliedProjectTagResponse),
    answer_states: z.record(AnswerStateResponse).nullable(),
})
    .passthrough());
export const getTeamsProjectsRoute = {
    version: 1,
    method: 'GET',
    path: '/teams/projects/',
    name: 'get-teams-projects',
    request: {},
    responses: { 200: GetTeamsProjectsResponse, 400: HttpErrorResponse },
};
export const TeamsProfileResponse = z
    .object({ hasProfile: z.boolean() })
    .passthrough();
export const getTeamsProfileRoute = {
    version: 1,
    method: 'GET',
    path: '/teams/profile/',
    name: 'get-teams-profile',
    request: {},
    responses: { 200: TeamsProfileResponse, 400: HttpErrorResponse },
};
export const GetAssessmentTestResponse = z.object({
    assessmentTestId: z.number(),
    companyId: z.number(),
    assessmentTestSessionId: z.number(),
    steps: z.array(AssessmentTestStepResponse),
    answers: z.array(AssessmentTestAnswerResponse),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    finishReason: z.enum(['COMPLETED', 'TIME_EXPIRED']),
});
export const getAssessmentTestRoute = {
    version: 1,
    method: 'GET',
    path: '/teams/assessments/slug/{atCompanySlug}/tests/{atTestSlug}',
    name: 'get-assessment-test',
    request: {
        params: z.object({
            atCompanySlug: CompanySlug,
            atTestSlug: AssessmentTestSlug,
        }),
    },
    responses: {
        200: GetAssessmentTestResponse,
        400: HttpErrorResponse,
        404: HttpErrorResponse,
    },
};
